/* ─────────────────────────────────────────────────────────────
   VALUATION HISTORY SECTIONS
   GET /api/v1/stocks/{ticker}/valuation-history?limit=

   One endpoint, three series — each rendered as a self-contained section so
   it can live on whichever tab it belongs to:

     <IntrinsicValueHistory />  → Intrinsic Value tab
     <FairValueHistory />       → Fair Value tab
     <RatingsHistory />         → FA Ratings tab

   Each section fetches independently with its own row limit. That means up to
   three calls for the same payload, but the endpoint is cached server-side for
   15 minutes and only one tab is mounted at a time, so the cost is negligible.

   These render BARE sections (no page wrapper) so they drop cleanly into
   host tabs that already own their own padding.
───────────────────────────────────────────────────────────── */

import React, { useEffect, useState } from "react";
import { getStockValuationHistory } from "../../../api/stocks_api";
import { formatCurrency } from "../../../utils/currency";
import {
  RATINGS_FACTOR_ROWS,
  RATINGS_FACTOR_FULL_LABELS,
  confidenceTone,
  methodLabel,
  decimalAsPct,
} from "../../../config/valuation";
import { fmtNum, fmtPct, fmtDate, signalTone, ratingTone } from "../../../config/earningsNote";
import {
  SectionTitle, Banner, GradeChip, Scroller, Table, Th, Td, Tl,
} from "./earningsNoteUI";

/* The API's `limit` is 1–100 and there is no offset/cursor parameter, so 100
   rows is a hard ceiling — not a UI choice. When a request comes back full at
   the maximum, callers surface API_LIMIT_NOTE so the cap is not mistaken for
   "no more history". */
const HISTORY_LIMITS = [10, 25, 50, 100];
const MAX_LIMIT = 100;
const API_LIMIT_NOTE =
  "The history endpoint returns at most 100 rows per request and has no paging parameter, so snapshots older than these are not reachable yet.";

/* Positive percentage = good. */
const upsideTone = (v) => (v == null ? "muted" : Number(v) >= 0 ? "pos" : "neg");

const SNAPSHOT_NOTE =
  "True-value and fair-value snapshots began accumulating one row per day on 10 Aug 2026, so these series are short for now and grow daily.";

/* ── Shared fetch + row-limit control ── */
const useHistorySeries = (ticker, seriesKey) => {
  const [limit, setLimit] = useState(HISTORY_LIMITS[0]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!ticker) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const res = await getStockValuationHistory(ticker, limit);
      if (cancelled) return;
      setData(res?.success ? res.data : null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [ticker, limit]);

  const rows = data?.[seriesKey] ?? [];
  // At the ceiling AND full → there may be more history the API cannot return.
  const atApiCap = limit === MAX_LIMIT && rows.length >= MAX_LIMIT;

  return { rows, loading, limit, setLimit, atApiCap };
};

const LimitSelector = ({ limit, setLimit }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: "var(--spacing-sm)" }}>
    <span style={{ color: "var(--color-text-muted)", fontSize: "var(--font-size-xs)", fontWeight: 500 }}>
      Rows
    </span>
    {HISTORY_LIMITS.map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => setLimit(n)}
        aria-pressed={limit === n}
        style={{
          padding: "4px 12px",
          borderRadius: "var(--radius-md)",
          border: `1px solid ${limit === n ? "var(--color-primary)" : "var(--color-border-primary)"}`,
          backgroundColor: limit === n ? "var(--color-primary)" : "var(--color-surface)",
          color: limit === n ? "#FFFFFF" : "var(--color-text-secondary)",
          fontSize: "var(--font-size-xs)",
          fontWeight: "var(--font-weight-semibold)",
          cursor: "pointer",
        }}
      >
        {n}
      </button>
    ))}
  </div>
);

const Fading = ({ loading, children }) => (
  <div style={{ opacity: loading ? 0.55 : 1, transition: "var(--transition-fast)" }}>{children}</div>
);

/* ══════════════════════════════════════════════════════════════
   1. INTRINSIC VALUE HISTORY  → Intrinsic Value tab
══════════════════════════════════════════════════════════════ */
export const IntrinsicValueHistory = ({ ticker }) => {
  const { rows, loading, limit, setLimit, atApiCap } = useHistorySeries(ticker, "intrinsic_value_history");

  if (!loading && rows.length === 0) {
    return (
      <>
        <SectionTitle>True-value history</SectionTitle>
        <Banner kind="plain">No true-value snapshots have been recorded for {ticker} yet.</Banner>
      </>
    );
  }

  return (
    <>
      <SectionTitle caption="newest first">True-value history</SectionTitle>
      <LimitSelector limit={limit} setLimit={setLimit} />
      <Fading loading={loading}>
        <Scroller>
          <Table>
            <thead>
              <tr>
                <Th left>As of</Th>
                <Th>TV / share</Th>
                <Th>Margin of safety</Th>
                <Th>Upside</Th>
                <Th>Discount rate</Th>
                <Th>Risk-free</Th>
                <Th>ERP</Th>
                <Th>Terminal g</Th>
                <Th>Growth used</Th>
                <Th left>Method</Th>
                <Th left>Confidence</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.as_of_date ?? i}>
                  <Tl>{fmtDate(r.as_of_date)}</Tl>
                  <Td bold>{formatCurrency(r.iv_per_share)}</Td>
                  <Td tone={upsideTone(r.margin_of_safety_pct)}>
                    {fmtPct(r.margin_of_safety_pct, { decimals: 1 })}
                  </Td>
                  <Td tone={upsideTone(r.upside_downside_pct)}>
                    {fmtPct(r.upside_downside_pct, { decimals: 1 })}
                  </Td>
                  <Td>{decimalAsPct(r.discount_rate, 2)}</Td>
                  <Td tone="muted">{decimalAsPct(r.risk_free_rate, 2)}</Td>
                  <Td tone="muted">{decimalAsPct(r.equity_risk_premium, 2)}</Td>
                  <Td tone="muted">{decimalAsPct(r.terminal_growth_rate, 2)}</Td>
                  <Td tone="muted">{decimalAsPct(r.growth_rate_used, 2)}</Td>
                  <Td left tone="muted">{methodLabel(r.calculation_method) ?? "—"}</Td>
                  <Td left tone={confidenceTone(r.confidence)} bold>
                    {r.confidence ?? "—"}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroller>
        <Banner kind="plain" style={{ marginTop: "var(--spacing-sm)" }}>
          {SNAPSHOT_NOTE}
          {atApiCap ? ` ${API_LIMIT_NOTE}` : ""}
        </Banner>
      </Fading>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════
   2. FAIR VALUE + ANALYST ESTIMATES  → Fair Value tab

   The analyst-estimate columns sit adjacent to the fair value in every row so
   estimate movement reads next to the fair value it feeds.
══════════════════════════════════════════════════════════════ */
export const FairValueHistory = ({ ticker }) => {
  const { rows, loading, limit, setLimit, atApiCap } = useHistorySeries(ticker, "fair_value_history");

  if (!loading && rows.length === 0) {
    return (
      <>
        <SectionTitle>Fair value &amp; analyst estimates</SectionTitle>
        <Banner kind="plain">No fair-value snapshots have been recorded for {ticker} yet.</Banner>
      </>
    );
  }

  return (
    <>
      <SectionTitle caption="newest first">Fair value &amp; analyst estimates</SectionTitle>
      <LimitSelector limit={limit} setLimit={setLimit} />
      <Fading loading={loading}>
        <Scroller>
          <Table>
            <thead>
              <tr>
                <Th left>As of</Th>
                <Th>Fair value</Th>
                <Th>FV TV</Th>
                <Th>FV analyst</Th>
                <Th>Fwd EPS</Th>
                <Th>EPS TTM</Th>
                <Th>EPS analysts</Th>
                <Th>Targets 3m / 12m</Th>
                <Th left>Consensus</Th>
                <Th>Band</Th>
                <Th>Upside to FV</Th>
                <Th left>Signal</Th>
                <Th>Price used</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.as_of_date ?? i}>
                  <Tl>{fmtDate(r.as_of_date)}</Tl>
                  <Td bold>{formatCurrency(r.fair_value)}</Td>
                  <Td>{r.fv_iv != null ? formatCurrency(r.fv_iv) : "—"}</Td>
                  <Td>{r.fv_analyst != null ? formatCurrency(r.fv_analyst) : "—"}</Td>
                  <Td tone="muted">{r.fwd_eps != null ? formatCurrency(r.fwd_eps) : "—"}</Td>
                  <Td tone="muted">{r.eps_ttm != null ? formatCurrency(r.eps_ttm) : "—"}</Td>
                  <Td tone="muted">{r.num_eps_analysts ?? "—"}</Td>
                  <Td tone="muted">
                    {r.num_target_analysts_3m ?? "—"} / {r.num_target_analysts_12m ?? "—"}
                  </Td>
                  <Td left tone="muted">{r.analyst_consensus ?? "—"}</Td>
                  <Td tone="muted">{decimalAsPct(r.band, 1)}</Td>
                  <Td bold tone={upsideTone(r.upside_to_fv)}>
                    {fmtPct(r.upside_to_fv, { decimals: 1 })}
                  </Td>
                  <Td left bold tone={signalTone(r.signal)}>{r.signal ?? "—"}</Td>
                  <Td tone="muted">{r.price_used != null ? formatCurrency(r.price_used) : "—"}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroller>
        <div
          style={{
            marginTop: 6,
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-xs)",
            lineHeight: 1.6,
          }}
        >
          Fair value is the mean of the non-null true-value and analyst-target anchors. {SNAPSHOT_NOTE}
          {atApiCap ? ` ${API_LIMIT_NOTE}` : ""}
        </div>
      </Fading>
    </>
  );
};

/* ══════════════════════════════════════════════════════════════
   3. FACTOR RATINGS HISTORY  → FA Ratings tab

   `score` is the 0–100 overall. Per-FACTOR score values are model z-scores,
   so only the grade letters are shown.
══════════════════════════════════════════════════════════════ */
export const RatingsHistory = ({ ticker }) => {
  const { rows, loading, limit, setLimit, atApiCap } = useHistorySeries(ticker, "ratings_history");

  if (!loading && rows.length === 0) return null;

  return (
    <div style={{ marginTop: "var(--spacing-lg)" }}>
      <SectionTitle caption="newest first">Factor-ratings history</SectionTitle>
      <LimitSelector limit={limit} setLimit={setLimit} />
      <Fading loading={loading}>
        <Scroller>
          <Table>
            <thead>
              <tr>
                <Th left>Rated</Th>
                <Th left>Rating</Th>
                <Th>Score</Th>
                <Th>Grade</Th>
                {RATINGS_FACTOR_ROWS.map((f) => (
                  <Th key={f.key} style={{ textAlign: "center" }}>
                    <span title={RATINGS_FACTOR_FULL_LABELS[f.key]}>{f.label}</span>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.rating_date ?? i}>
                  <Tl>{fmtDate(r.rating_date)}</Tl>
                  <Td left bold tone={ratingTone(r.rating)}>{r.rating ?? "—"}</Td>
                  <Td>{fmtNum(r.score, 1)}</Td>
                  <Td>
                    <GradeChip grade={r.grade} />
                  </Td>
                  {RATINGS_FACTOR_ROWS.map((f) => (
                    <Td key={f.key} style={{ textAlign: "center" }}>
                      <GradeChip grade={r[f.key]?.grade} />
                    </Td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </Scroller>
        <div
          style={{
            marginTop: 6,
            color: "var(--color-text-muted)",
            fontSize: "var(--font-size-xs)",
            lineHeight: 1.6,
          }}
        >
          Score is the 0–100 overall. Per-factor values are model z-scores, so only the grade
          letters are shown. Ratings history goes back as far as the rating engine has run.
          {atApiCap ? ` ${API_LIMIT_NOTE}` : ""}
        </div>
      </Fading>
    </div>
  );
};
